(() => {
  'use strict';

  const mapsState = window.__fafGoogleMapsState || {
    promise: null,
    key: ''
  };
  window.__fafGoogleMapsState = mapsState;

  const loadGoogleMaps = (apiKey) => {
    if (window.google?.maps) return Promise.resolve(window.google.maps);
    if (!apiKey) return Promise.reject(new Error('missing-key'));
    if (mapsState.promise) return mapsState.promise;

    mapsState.key = apiKey;
    mapsState.promise = new Promise((resolve, reject) => {
      const existingScript = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');
      const handleLoad = () => {
        if (window.google?.maps) {
          resolve(window.google.maps);
        } else {
          reject(new Error('load-failed'));
        }
      };

      if (existingScript) {
        existingScript.addEventListener('load', handleLoad, { once: true });
        existingScript.addEventListener('error', () => reject(new Error('load-failed')), { once: true });
        window.setTimeout(() => {
          if (window.google?.maps) resolve(window.google.maps);
        }, 0);
        return;
      }

      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}`;
      script.async = true;
      script.defer = true;
      script.onload = handleLoad;
      script.onerror = () => reject(new Error('load-failed'));
      document.head.appendChild(script);
    });

    return mapsState.promise;
  };

  const toNumber = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
  };

  const normalise = (value) => String(value || '').trim().toLowerCase();

  const escapeText = (value) => String(value || '').replace(/[&<>'"]/g, (character) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    "'": '&#39;',
    '"': '&quot;'
  })[character]);

  const distanceBetween = (origin, destination, unit) => {
    const radians = Math.PI / 180;
    const lat1 = origin.lat * radians;
    const lat2 = destination.lat * radians;
    const deltaLat = (destination.lat - origin.lat) * radians;
    const deltaLng = (destination.lng - origin.lng) * radians;
    const a = Math.sin(deltaLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLng / 2) ** 2;
    const kilometres = 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return unit === 'kilometres' ? kilometres : kilometres / 1.609344;
  };

  const detectCountryOrder = (query, countries) => {
    const cleaned = String(query || '').trim();
    const ukPattern = /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i;
    const usPattern = /^\d{5}(?:-\d{4})?$/;
    const preferred = ukPattern.test(cleaned) ? 'GB' : usPattern.test(cleaned) ? 'US' : '';
    const unique = [...new Set((countries || []).filter(Boolean).map((country) => country.toUpperCase()))];
    if (preferred && unique.includes(preferred)) return [preferred, ...unique.filter((country) => country !== preferred)];
    return unique;
  };

  class LocationDirectory {
    constructor(root) {
      this.root = root;
      this.config = this.readJson('[data-location-config]', {});
      this.items = this.readJson('[data-location-items]', []).map((item, index) => ({
        ...item,
        index,
        lat: toNumber(item.lat),
        lng: toNumber(item.lng),
        distance: null
      }));
      this.cards = new Map([...root.querySelectorAll('[data-location-card]')].map((card) => [card.dataset.locationId, card]));
      this.map = null;
      this.infoWindow = null;
      this.markers = new Map();
      this.userMarker = null;
      this.userPosition = null;
      this.visibleItems = [...this.items];
      this.mapOpen = root.dataset.mapInitiallyOpen === 'true';
      this.mapLoaded = false;
      this.mapLoading = false;
      this.mapObserver = null;

      this.searchInput = root.querySelector('[data-location-search]');
      this.monthInput = root.querySelector('[data-location-month]');
      this.regionInput = root.querySelector('[data-location-region]');
      this.typeInput = root.querySelector('[data-location-type]');
      this.locationInput = root.querySelector('[data-location-query]');
      this.mapPanel = root.querySelector('[data-location-map-panel]');
      this.mapCanvas = root.querySelector('[data-location-map]');
      this.resultCount = root.querySelector('[data-location-count]');
      this.emptyState = root.querySelector('[data-location-empty]');
      this.status = root.querySelector('[data-location-status]');
      this.mapToggle = root.querySelector('[data-location-map-toggle]');
      this.clearButton = root.querySelector('[data-location-clear]');

      this.bind();
      this.applyFilters();
      this.setMapOpen(this.mapOpen, false, this.mapOpen);
    }

    readJson(selector, fallback) {
      const node = this.root.querySelector(selector);
      if (!node) return fallback;
      try {
        return JSON.parse(node.textContent || '');
      } catch (error) {
        console.warn('Unable to read location data.', error);
        return fallback;
      }
    }

    bind() {
      [this.searchInput, this.monthInput, this.regionInput, this.typeInput].forEach((control) => {
        control?.addEventListener(control.tagName === 'INPUT' ? 'input' : 'change', () => this.applyFilters());
      });

      this.clearButton?.addEventListener('click', () => {
        if (this.searchInput) this.searchInput.value = '';
        if (this.monthInput) this.monthInput.value = '';
        if (this.typeInput) this.typeInput.value = '';
        this.userPosition = null;
        this.items.forEach((item) => { item.distance = null; });
        this.applyFilters();
      });

      this.mapToggle?.addEventListener('click', () => this.setMapOpen(!this.mapOpen));
      this.root.querySelector('[data-location-submit]')?.addEventListener('click', () => this.lookupLocation());
      this.locationInput?.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          this.lookupLocation();
        }
      });
      this.root.querySelector('[data-location-geolocate]')?.addEventListener('click', () => this.useCurrentLocation());

      this.root.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-location-focus]');
        if (!trigger) return;
        const item = this.items.find((entry) => entry.id === trigger.dataset.locationFocus);
        if (item) this.focusItem(item);
      });
    }

    setStatus(message = '', tone = 'neutral') {
      if (!this.status) return;
      this.status.textContent = message;
      this.status.dataset.tone = tone;
      this.status.hidden = !message;
    }

    selectedUnit() {
      if (this.config.distanceUnit === 'kilometres') return 'kilometres';
      if (this.config.distanceUnit === 'miles') return 'miles';
      return this.config.activeRegion === 'us' ? 'miles' : 'miles';
    }

    filterItem(item) {
      const query = normalise(this.searchInput?.value);
      const month = this.monthInput?.value || '';
      const region = this.regionInput?.value || this.config.activeRegion || 'all';
      const type = this.typeInput?.value || '';
      if (query && !normalise(item.searchText).includes(query)) return false;
      if (month && item.month !== month) return false;
      if (region !== 'all' && item.region !== 'all' && item.region !== region) return false;
      if (type && item.type !== type) return false;
      return true;
    }

    applyFilters() {
      this.visibleItems = this.items.filter((item) => this.filterItem(item));
      this.visibleItems.sort((a, b) => {
        if (this.userPosition) {
          const aDistance = a.distance ?? Number.POSITIVE_INFINITY;
          const bDistance = b.distance ?? Number.POSITIVE_INFINITY;
          if (aDistance !== bDistance) return aDistance - bDistance;
        }
        if (this.config.sortMode === 'date') {
          const aDate = Number(a.start || 0);
          const bDate = Number(b.start || 0);
          if (aDate !== bDate) return aDate - bDate;
        }
        return a.index - b.index;
      });

      const visibleIds = new Set(this.visibleItems.map((item) => item.id));
      this.items.forEach((item) => {
        const card = this.cards.get(item.id);
        if (!card) return;
        const visible = visibleIds.has(item.id);
        card.hidden = !visible;
        card.style.order = visible ? String(this.visibleItems.indexOf(item)) : '';
        const distance = card.querySelector('[data-location-distance]');
        if (distance) {
          if (item.distance == null) {
            distance.hidden = true;
          } else {
            const rounded = item.distance < 10 ? item.distance.toFixed(1) : Math.round(item.distance).toString();
            const label = this.selectedUnit() === 'kilometres' ? this.config.kilometresLabel : this.config.milesLabel;
            distance.textContent = `${rounded} ${label}`;
            distance.hidden = false;
          }
        }
      });

      if (this.resultCount) {
        const count = this.visibleItems.length;
        this.resultCount.textContent = `${count} ${count === 1 ? this.config.resultSingular : this.config.resultPlural}`;
      }
      if (this.emptyState) this.emptyState.hidden = this.visibleItems.length > 0;
      if (this.clearButton) {
        this.clearButton.hidden = !this.searchInput?.value && !this.monthInput?.value && !this.typeInput?.value && !this.userPosition;
      }
      if (this.mapLoaded) this.renderMarkers();
    }

    async setMapOpen(open, focus = true, deferLoad = false) {
      this.mapOpen = Boolean(open);
      if (this.mapPanel) this.mapPanel.hidden = !this.mapOpen;
      this.mapToggle?.setAttribute('aria-expanded', this.mapOpen ? 'true' : 'false');
      this.root.querySelectorAll('[data-map-open-label]').forEach((node) => { node.hidden = this.mapOpen; });
      this.root.querySelectorAll('[data-map-close-label]').forEach((node) => { node.hidden = !this.mapOpen; });

      if (!this.mapOpen) {
        this.mapObserver?.disconnect();
        this.mapObserver = null;
        return;
      }

      if (deferLoad && !this.mapLoaded) {
        this.deferMapLoad();
        return;
      }

      await this.ensureMap();
      window.setTimeout(() => {
        if (this.map) {
          google.maps.event.trigger(this.map, 'resize');
          this.fitVisibleItems();
        }
        if (focus) this.mapCanvas?.focus({ preventScroll: true });
      }, 0);
    }

    deferMapLoad() {
      if (!this.mapCanvas || this.mapLoaded || this.mapLoading || this.mapObserver) return;
      if (!('IntersectionObserver' in window)) {
        this.ensureMap();
        return;
      }

      this.mapObserver = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.mapObserver?.disconnect();
        this.mapObserver = null;
        this.ensureMap();
      }, { rootMargin: '320px 0px' });

      this.mapObserver.observe(this.mapCanvas);
    }

    async ensureMap() {
      if (this.mapLoaded || this.mapLoading || !this.mapCanvas) return;
      this.mapLoading = true;
      this.setStatus(this.config.loadingLabel || 'Loading map…');
      try {
        await loadGoogleMaps(this.config.apiKey);
        const centre = this.config.mapCentre || { lat: 54.5, lng: -3.5 };
        this.map = new google.maps.Map(this.mapCanvas, {
          center: centre,
          zoom: Number(this.config.mapZoom || 5),
          maxZoom: Number(this.config.maxZoom || 16),
          scrollwheel: false,
          gestureHandling: 'cooperative',
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
          styles: this.config.mapStyles || []
        });
        this.infoWindow = new google.maps.InfoWindow();
        this.mapLoaded = true;
        this.renderMarkers();
        this.setStatus(this.config.readyLabel || 'Map ready.');
      } catch (error) {
        this.setStatus(this.config.errorLabel || 'The map could not be loaded.', 'error');
      } finally {
        this.mapLoading = false;
      }
    }

    markerIcon(item) {
      return {
        url: item.pin || this.config.defaultPin,
        scaledSize: new google.maps.Size(33, 44),
        size: new google.maps.Size(33, 44),
        anchor: new google.maps.Point(16.5, 44)
      };
    }

    renderMarkers() {
      if (!this.mapLoaded) return;
      const visibleIds = new Set(this.visibleItems.filter((item) => item.lat != null && item.lng != null).map((item) => item.id));

      this.markers.forEach((marker, id) => {
        if (!visibleIds.has(id)) {
          marker.setMap(null);
          this.markers.delete(id);
        }
      });

      this.visibleItems.forEach((item) => {
        if (item.lat == null || item.lng == null) return;
        let marker = this.markers.get(item.id);
        if (!marker) {
          marker = new google.maps.Marker({
            map: this.map,
            position: { lat: item.lat, lng: item.lng },
            title: item.title,
            icon: this.markerIcon(item),
            optimized: true
          });
          marker.addListener('click', () => this.openInfo(item, marker));
          this.markers.set(item.id, marker);
        } else {
          marker.setMap(this.map);
        }
      });

      this.renderUserMarker();
      this.fitVisibleItems();
    }

    renderUserMarker() {
      if (!this.mapLoaded || !this.userPosition) {
        this.userMarker?.setMap(null);
        this.userMarker = null;
        return;
      }
      if (!this.userMarker) {
        this.userMarker = new google.maps.Marker({
          map: this.map,
          position: this.userPosition,
          title: this.config.userLocationLabel || 'Your location',
          zIndex: 999
        });
      } else {
        this.userMarker.setPosition(this.userPosition);
        this.userMarker.setMap(this.map);
      }
    }

    openInfo(item, marker) {
      if (!this.infoWindow) return;
      const distance = item.distance == null ? '' : `<p style="margin:6px 0 0;color:#b28a4a;font-size:12px">${escapeText(this.cards.get(item.id)?.querySelector('[data-location-distance]')?.textContent || '')}</p>`;
      const action = item.url ? `<p style="margin:10px 0 0"><a href="${escapeText(item.url)}" style="color:#1e293a;text-decoration:underline">${escapeText(this.config.detailsLabel || 'View details')}</a></p>` : '';
      this.infoWindow.setContent(`<div style="max-width:260px;color:#1e293a"><strong>${escapeText(item.title)}</strong><p style="margin:7px 0 0;white-space:pre-line;color:#666;font-size:12px;line-height:1.45">${escapeText(item.address)}</p>${distance}${action}</div>`);
      this.infoWindow.open({ map: this.map, anchor: marker });
    }

    fitVisibleItems() {
      if (!this.mapLoaded) return;
      const mappable = this.visibleItems.filter((item) => item.lat != null && item.lng != null);
      if (!mappable.length) return;
      if (mappable.length === 1) {
        this.map.setCenter({ lat: mappable[0].lat, lng: mappable[0].lng });
        this.map.setZoom(Math.min(Number(this.config.singleZoom || 12), Number(this.config.maxZoom || 16)));
        return;
      }
      const bounds = new google.maps.LatLngBounds();
      mappable.forEach((item) => bounds.extend({ lat: item.lat, lng: item.lng }));
      if (this.userPosition) bounds.extend(this.userPosition);
      this.map.fitBounds(bounds, 48);
      google.maps.event.addListenerOnce(this.map, 'idle', () => {
        if (this.map.getZoom() > Number(this.config.maxZoom || 16)) this.map.setZoom(Number(this.config.maxZoom || 16));
      });
    }

    async geocode(query) {
      await this.ensureMap();
      if (!this.mapLoaded) throw new Error('map-unavailable');
      const geocoder = new google.maps.Geocoder();
      const countries = detectCountryOrder(query, this.config.countries || []);
      const attempts = countries.map((country) => ({ address: query, componentRestrictions: { country } }));
      attempts.push({ address: query });

      for (const request of attempts) {
        try {
          const response = await geocoder.geocode(request);
          if (response.results?.[0]) {
            const location = response.results[0].geometry.location;
            return { lat: location.lat(), lng: location.lng() };
          }
        } catch (error) {
          if (error?.code && error.code !== 'ZERO_RESULTS') throw error;
        }
      }
      throw new Error('not-found');
    }

    applyOrigin(position) {
      this.userPosition = position;
      const unit = this.selectedUnit();
      this.items.forEach((item) => {
        item.distance = item.lat == null || item.lng == null ? null : distanceBetween(position, item, unit);
      });
      this.applyFilters();
      this.setMapOpen(true, false);
      if (this.mapLoaded) {
        this.map.setCenter(position);
        this.fitVisibleItems();
      }
    }

    async lookupLocation() {
      const query = this.locationInput?.value.trim();
      if (!query) {
        this.setStatus(this.config.locationRequiredLabel || 'Enter a postcode or location.', 'error');
        this.locationInput?.focus();
        return;
      }
      this.setStatus(this.config.locatingLabel || 'Finding your location…');
      try {
        const position = await this.geocode(query);
        this.applyOrigin(position);
        this.setStatus(this.config.locationReadyLabel || 'Locations are now ordered by distance.');
      } catch (error) {
        this.setStatus(this.config.locationNotFoundLabel || 'We could not find that location.', 'error');
      }
    }

    useCurrentLocation() {
      if (!navigator.geolocation) {
        this.setStatus(this.config.geolocationErrorLabel || 'Location services are unavailable.', 'error');
        return;
      }
      this.setStatus(this.config.locatingLabel || 'Finding your location…');
      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.applyOrigin({ lat: position.coords.latitude, lng: position.coords.longitude });
          this.setStatus(this.config.locationReadyLabel || 'Locations are now ordered by distance.');
        },
        () => this.setStatus(this.config.geolocationErrorLabel || 'We could not access your location.', 'error'),
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
      );
    }

    async focusItem(item) {
      await this.setMapOpen(true, false);
      const marker = this.markers.get(item.id);
      if (!marker || !this.map) return;
      this.map.panTo(marker.getPosition());
      this.map.setZoom(Math.min(Number(this.config.singleZoom || 12), Number(this.config.maxZoom || 16)));
      this.openInfo(item, marker);
      this.mapCanvas?.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
    }
  }

  const initialise = (scope = document) => {
    scope.querySelectorAll('[data-faf-location-directory]:not([data-location-ready])').forEach((root) => {
      root.dataset.locationReady = 'true';
      new LocationDirectory(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialise());
  } else {
    initialise();
  }
  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
})();
